import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const SEARCH_STUCK_CLASS = "fly-availability__searchDispo-container--stuck";
const SEARCH_NON_STICKY_CLASS =
  "fly-availability__searchDispo-container--non-sticky";
const SEARCH_CURTAIN_HIDDEN_CLASS =
  "fly-availability__searchDispo-container--curtain-hidden";
const SEARCH_CURTAIN_REVEALING_CLASS =
  "fly-availability__searchDispo-container--curtain-revealing";
const SEARCH_CURTAIN_COLLAPSED_CLASS =
  "fly-availability__searchDispo-container--curtain-collapsed";
const SEARCH_SUMMARY_REVEALED_CLASS =
  "fly-availability__searchDispo-container--summary-revealed";
const SEARCH_SUMMARY_MEASURE_CLASS =
  "fly-availability__searchDispo-container--summary-measure";
const SEARCH_MOBILE_PINNED_CLASS =
  "fly-availability__searchDispo-container--mobile-pinned";
const SUMMARY_SEARCH_MODE_CLASS = "fly-availability--summary-search-mode";
const SUMMARY_STUCK_CLASS = "fly-summaryBarB2C--stuck";
const STICKY_INFO_STUCK_CLASS = "fly-stickyInfoB2C--stuck";
const STICKY_INFO_STOPPED_CLASS = "fly-stickyInfoB2C--stopped";
const CARD_HOVER_CLASS = "fly-cardB2C--hovered";
const CARD_GRID_BOTTOM_CLASS = "fly-cardB2C--grid-bottom";
const MOBILE_SUMMARY_EXITING_CLASS =
  "fly-availability__summary-bar--mobile-exiting";
const MOBILE_SUMMARY_MQ = "(max-width: 810px)";
const MOBILE_STICKY_INFO_MQ = "(max-width: 767px)";

const CARD_HOVER_DELAY_MS = 180;

/**
 * Scroll sticky + sombras en dispo de vuelos (buscador, summary bar, sticky info RT)
 * y zoom suave en cards al hover (comportamiento prototipo Vuelos 4mayo).
 */
export class FlyAvailabilityLayout implements Plugin {
  private scrollHandler: (() => void) | null = null;
  private resizeHandler: (() => void) | null = null;
  private readonly stickyPlaceholders = new WeakMap<HTMLElement, HTMLElement>();
  private cardHoverCleanups: Array<() => void> = [];
  private mobileSummaryMq: MediaQueryList | null = null;
  private mobileSummaryMqHandler:
    | ((event: MediaQueryListEvent) => void)
    | null = null;
  private mobileSummaryAnimationEndHandler: (() => void) | null = null;
  private searchCurtainAnimationEndHandler:
    | ((event: AnimationEvent | TransitionEvent) => void)
    | null = null;
  private journeyExitRevealHandler: ((event: AnimationEvent) => void) | null =
    null;
  private journeyExitRevealFallbackTimer: number | null = null;
  private searchHiddenByJourney = false;
  private horizontalScrollCleanups: Array<() => void> = [];
  private lastScrollY = 0;
  /** Con summary visible: buscador sticky solo tras scroll hacia arriba. */
  private summaryModeSearchRevealed = false;
  private cachedSummaryModeSearchHeight = 0;
  private summaryModeSearchTransitionUntil = 0;
  private summaryModeCardsOffsetDirty = false;
  /**
   * Tras el primer journey sticky, el buscador pasa a modo “revelar/ocultar”
   * con scroll hasta volver al top de la página.
   */
  private summaryModeJourneyScrollEngaged = false;
  private summaryModeJourneyEngagedReady = false;
  private summaryModeEngageSuppressUntil = 0;
  private lastJourneyLayoutStateKey = "";
  private summaryModeRevealWhenUnlocked = false;
  /** Tras stuck/stopped el scrollY puede bajar sin intención de subir (p. ej. fin de journey). */
  private summaryModeLayoutSuppressUntil = 0;
  private summaryModeLastJourneyStickyInDom = false;
  private summaryModeMobileRepinDebounceUntil = 0;
  /**
   * ≤810px: tras el 1.er handoff buscador→journey el buscador no vuelve al bajar
   * aunque el journey deje de cubrir (solo scroll arriba).
   */
  private mobileSummarySearchHandoffLatched = false;
  /** Máximo scrollY alcanzado bajando — para distinguir scroll-up real de scroll anchoring. */
  private mobileSummaryPeakScrollY = 0;
  private lastAppliedCardsStickyTop = -1;
  private summaryModeWheelHandler: ((event: WheelEvent) => void) | null = null;
  private static readonly SEARCH_REVEAL_PROGRESS_VAR =
    "--fly-dispo-search-reveal-progress";
  private static readonly SEARCH_REVEAL_MAX_H_VAR =
    "--fly-summary-search-reveal-max-h";
  private static readonly SEARCH_MOTION_IN_CLASS =
    "fly-dispo-summary-search-motion-in";
  private static readonly SEARCH_MOTION_OUT_CLASS =
    "fly-dispo-summary-search-motion-out";
  private static readonly SUMMARY_SEARCH_TRANSITION_LOCK_MS = 480;
  private static readonly SUMMARY_SEARCH_CURTAIN_FALLBACK_MS = 500;
  /** Ignora deltas de scroll causados por el layout al enganchar journey sticky. */
  private static readonly SUMMARY_SEARCH_ENGAGE_SUPPRESS_MS = 500;
  /** Mínimo desplazamiento (px) para interpretar scroll como intención arriba/abajo. */
  private static readonly SUMMARY_SCROLL_INTENT_MIN_DELTA = 2;
  /** Ignora “scroll arriba” espurio tras cambios stuck/stopped (p. ej. scroll anchoring). */
  private static readonly SUMMARY_LAYOUT_SUPPRESS_MS = 450;
  /** Evita parpadeo pin/ocultar cuando el sticky del journey oscila en el umbral. */
  private static readonly MOBILE_REPIN_DEBOUNCE_MS = 220;
  private static readonly MOBILE_STICKY_HYSTERESIS_PX = 6;
  /** Px que el usuario debe subir por debajo del pico para revelar el buscador en mobile handoff. */
  private static readonly MOBILE_SCROLL_UP_REVEAL_PX = 1;

  constructor(private readonly element: HTMLElement) {}

  private isSkeletonMode(): boolean {
    return (
      this.element.querySelector(".fly-availability__wrapperAB.skeleton") !==
      null
    );
  }

  init(): void {
    this.updateSearchStickyMode();
    this.updateAllStickyShadows();
    this.markGridBottomCards();
    this.setupMobileSummaryAnimation();

    this.scrollHandler = () => this.updateAllStickyShadows();
    this.resizeHandler = () => {
      this.cachedSummaryModeSearchHeight = 0;
      const search = this.getSearchEl();
      if (search && this.isSummaryVisible()) {
        this.ensureSummaryModeSearchHeightCached(search);
        this.syncSummaryModeFixedSearchGeometry(search);
      }
      this.updateSearchStickyMode();
      this.updateAllStickyShadows();
      this.markGridBottomCards();
      if (!this.isSkeletonMode()) {
        this.setupHorizontalScrollSync();
      }
    };

    window.addEventListener("scroll", this.scrollHandler, { passive: true });
    window.addEventListener("resize", this.resizeHandler, { passive: true });

    if (!this.isSkeletonMode()) {
      this.setupCardHoverDelay();
      this.setupHorizontalScrollSync();
    }

    this.setupSummaryModeWheelListener();
  }

  destroy(): void {
    const search = this.getSearchEl();
    if (search) {
      this.detachSearchCurtainAnimationHandler(search);
    }
    this.detachJourneyExitRevealHandlers();
    this.teardownHorizontalScrollSync();

    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
    this.teardownSummaryModeWheelListener();
    if (this.mobileSummaryMq && this.mobileSummaryMqHandler) {
      this.mobileSummaryMq.removeEventListener(
        "change",
        this.mobileSummaryMqHandler,
      );
      this.mobileSummaryMq = null;
      this.mobileSummaryMqHandler = null;
    }
    this.cardHoverCleanups.forEach((cleanup) => cleanup());
    this.cardHoverCleanups = [];
  }

  private getSearchEl(): HTMLElement | null {
    return this.element.querySelector(
      ".fly-availability__searchDispo-container",
    );
  }

  private getSearchDispoSlot(search: HTMLElement): HTMLElement | null {
    return search.querySelector(".fly-availability__searchDispo-slot");
  }

  /** Summary fijo arriba (desktop); en ≤810px el resumen va abajo en el dropdown mobile. */
  private usesMobileSummaryLayout(): boolean {
    if (typeof window.matchMedia !== "function") return false;
    return window.matchMedia(MOBILE_SUMMARY_MQ).matches;
  }

  /**
   * ≤810px con summary: handoff sticky estándar (buscador → journey).
   * >810px: modo summary-search (summary sticky + reveal por scroll).
   */
  private usesDesktopSummarySearchMode(): boolean {
    return this.isSummaryVisible() && !this.usesMobileSummaryLayout();
  }

  /**
   * ≤810px con summary abajo (dispo-summary): handoff buscador→journey como dispo
   * sin barra superior; no usa la pila sticky del modo desktop summary-search.
   */
  private usesMobileSummaryHandoffMode(): boolean {
    return (
      this.usesMobileSummaryLayout() && this.getMobileSummaryBar() !== null
    );
  }

  private teardownSummaryModeWheelListener(): void {
    if (!this.summaryModeWheelHandler) return;
    window.removeEventListener("wheel", this.summaryModeWheelHandler);
    this.summaryModeWheelHandler = null;
  }

  /** Mobile summary: sticky nativo + cortina; sin pin fijo ni engage anticipado. */
  private resetMobileSummarySearchToHandoff(search: HTMLElement): void {
    this.detachSearchCurtainAnimationHandler(search);
    this.summaryModeSearchRevealed = false;
    this.summaryModeJourneyScrollEngaged = false;
    this.summaryModeJourneyEngagedReady = false;
    this.summaryModeRevealWhenUnlocked = false;
    this.lastJourneyLayoutStateKey = "";
    this.summaryModeLayoutSuppressUntil = 0;
    this.summaryModeLastJourneyStickyInDom = false;
    this.summaryModeMobileRepinDebounceUntil = 0;
    this.mobileSummarySearchHandoffLatched = false;
    this.mobileSummaryPeakScrollY = 0;
    search.classList.remove(
      SEARCH_NON_STICKY_CLASS,
      SEARCH_MOBILE_PINNED_CLASS,
      SEARCH_SUMMARY_REVEALED_CLASS,
      SEARCH_SUMMARY_MEASURE_CLASS,
      SEARCH_CURTAIN_HIDDEN_CLASS,
      SEARCH_CURTAIN_REVEALING_CLASS,
      SEARCH_CURTAIN_COLLAPSED_CLASS,
      SEARCH_STUCK_CLASS,
    );
    this.clearSummaryModeFixedSearchGeometry(search);
    this.setSearchRevealProgress(0, { animate: false });
    this.searchHiddenByJourney = false;
  }

  /**
   * Mismo tick que dispo sin summary: journey sticky recalcula offset sin buscador
   * y entonces la cortina oculta el buscador (sustitución real en viewport).
   */
  private runStandardStickyHandoffTick(cardsStickyTop: number): void {
    const journeyShouldStick = this.updateBlockStickyShadow(cardsStickyTop);

    if (journeyShouldStick) {
      const cardsStickyTopWithoutSearch = this.getCardsStickyTop(true);
      this.applyCardsStickyOffset(true);
      this.updateBlockStickyShadow(cardsStickyTopWithoutSearch);
    } else {
      this.applyCardsStickyOffset(false);
    }
  }

  /**
   * Mobile summary: tres estados de offset para evitar flicks y desapariciones.
   *
   * · Buscador oculto (latched + hidden): cardsTop = 0, journeys a top:0
   * · Buscador visible (pre-handoff o revelado por scroll-up): cardsTop = searchH,
   *   journeys a top:searchH debajo del buscador. NO usa runStandardStickyHandoffTick
   *   (que haría 116→0 en un frame causando flick).
   */
  private runMobileSummaryStickyHandoffTick(): void {
    if (this.mobileSummarySearchHandoffLatched && this.searchHiddenByJourney) {
      this.applyCardsStickyOffset(true);
      this.updateBlockStickyShadow(0);
      return;
    }

    const searchTop = this.getCardsStickyTop(false);
    this.applyCardsStickyOffset(false);
    this.updateBlockStickyShadow(searchTop);
  }

  private getSummaryBar(): HTMLElement | null {
    if (this.usesMobileSummaryLayout()) return null;
    return this.element.querySelector(".fly-summaryBarB2C");
  }

  private getMobileSummaryBar(): HTMLElement | null {
    return this.element.querySelector(".fly-availability__summary-bar--mobile");
  }

  private getStepper(): HTMLElement | null {
    return this.element.querySelector(".fly-availability__stepper");
  }

  private getJourneyNav(): HTMLElement | null {
    return this.element.querySelector(".fly-availability__journey-nav");
  }

  /**
   * ≤767px con journey nav visible (dispo-summary): buscador y journey nav
   * quedan sticky arriba todo el rato y el sticky de tarifa de las cards
   * (FlyStickyInfoB2C) no se engancha; no hay handoff buscador→journey.
   */
  private usesJourneyNavStickyMode(): boolean {
    const journeyNav = this.getJourneyNav();
    return journeyNav !== null && journeyNav.offsetHeight > 0;
  }

  private runJourneyNavStickyTick(): void {
    const search = this.getSearchEl();
    if (search) {
      this.detachSearchCurtainAnimationHandler(search);
      search.classList.remove(
        SEARCH_CURTAIN_HIDDEN_CLASS,
        SEARCH_CURTAIN_REVEALING_CLASS,
        SEARCH_CURTAIN_COLLAPSED_CLASS,
      );
    }
    this.searchHiddenByJourney = false;
    this.mobileSummarySearchHandoffLatched = false;
    this.mobileSummaryPeakScrollY = 0;

    this.element
      .querySelectorAll<HTMLElement>(
        ".fly-rtDefaultB2C .fly-stickyInfoB2C, .fly-rtFeaturedB2C .fly-stickyInfoB2C, .fly-owDefaultB2C > .fly-stickyInfoB2C",
      )
      .forEach((stickyEl) => {
        stickyEl.classList.remove(
          STICKY_INFO_STUCK_CLASS,
          STICKY_INFO_STOPPED_CLASS,
        );
        stickyEl.style.removeProperty("--fly-sticky-stop-top");
        this.clearMobileFixedSticky(stickyEl);
      });

    // --fly-dispo-search-stack-h = alto del buscador → top del journey nav
    this.applyCardsStickyOffset(false);
    this.updateSearchStickyShadow(false);
  }

  private isSummaryVisible(): boolean {
    if (this.usesMobileSummaryLayout()) {
      const mobileSummary = this.getMobileSummaryBar();
      if (!mobileSummary) return false;
      const style = window.getComputedStyle(mobileSummary);
      return style.display !== "none" && mobileSummary.offsetHeight > 0;
    }

    const summary = this.element.querySelector(".fly-summaryBarB2C");
    if (!summary) return false;
    return summary.offsetParent !== null && summary.offsetHeight > 0;
  }

  /** Altura del summary que ocupa la pila sticky superior (0 en mobile). */
  private getSummaryStickyStackHeight(): number {
    if (!this.isSummaryVisible() || this.usesMobileSummaryLayout()) return 0;
    return this.getSummaryBar()?.offsetHeight ?? 0;
  }

  private usesMobileFixedSticky(): boolean {
    if (typeof window.matchMedia !== "function") return false;
    return window.matchMedia(MOBILE_STICKY_INFO_MQ).matches;
  }

  private clearMobileFixedSticky(stickyEl: HTMLElement): void {
    stickyEl.style.removeProperty("position");
    stickyEl.style.removeProperty("top");
    stickyEl.style.removeProperty("left");
    stickyEl.style.removeProperty("width");
    stickyEl.style.removeProperty("right");
    this.hideStickyPlaceholder(stickyEl);
  }

  private hideStickyPlaceholder(stickyEl: HTMLElement): void {
    const placeholder = this.stickyPlaceholders.get(stickyEl);
    if (placeholder) {
      placeholder.style.display = "none";
    }
  }

  private syncStickyPlaceholder(stickyEl: HTMLElement, active: boolean): void {
    if (!active) {
      this.hideStickyPlaceholder(stickyEl);
      return;
    }

    let placeholder = this.stickyPlaceholders.get(stickyEl);
    if (!placeholder) {
      placeholder = document.createElement("div");
      placeholder.className = "fly-stickyInfoB2C__placeholder";
      placeholder.setAttribute("aria-hidden", "true");
      stickyEl.parentElement?.insertBefore(placeholder, stickyEl);
      this.stickyPlaceholders.set(stickyEl, placeholder);
    }

    placeholder.style.height = `${stickyEl.offsetHeight}px`;
    placeholder.style.display = "block";
  }

  private getHorizontalScrollAncestors(block: HTMLElement): HTMLElement[] {
    const ancestors: HTMLElement[] = [];
    let parent: HTMLElement | null = block.parentElement;

    while (parent && parent !== this.element) {
      const { overflowX } = getComputedStyle(parent);
      if (
        overflowX === "auto" ||
        overflowX === "scroll" ||
        overflowX === "overlay"
      ) {
        ancestors.push(parent);
      }
      parent = parent.parentElement;
    }

    return ancestors;
  }

  private teardownHorizontalScrollSync(): void {
    this.horizontalScrollCleanups.forEach((cleanup) => cleanup());
    this.horizontalScrollCleanups = [];
  }

  private syncMobileFixedStickyPositions(): void {
    if (!this.usesMobileFixedSticky()) return;

    const stickySelector =
      ".fly-rtDefaultB2C .fly-stickyInfoB2C, .fly-rtFeaturedB2C .fly-stickyInfoB2C, .fly-owDefaultB2C > .fly-stickyInfoB2C";

    this.element
      .querySelectorAll<HTMLElement>(stickySelector)
      .forEach((stickyEl) => {
        if (!stickyEl.classList.contains(STICKY_INFO_STUCK_CLASS)) return;

        const block = stickyEl.closest(
          ".fly-rtDefaultB2C__group, .fly-rtFeaturedB2C__group, .fly-owDefaultB2C",
        ) as HTMLElement | null;
        if (!block) return;

        const blockRect = block.getBoundingClientRect();
        stickyEl.style.left = `${blockRect.left}px`;
        stickyEl.style.width = `${blockRect.width}px`;
      });
  }

  private setupHorizontalScrollSync(): void {
    this.teardownHorizontalScrollSync();

    if (!this.usesMobileFixedSticky()) return;

    const blocks = this.element.querySelectorAll<HTMLElement>(
      ".fly-rtDefaultB2C, .fly-rtFeaturedB2C, .fly-owDefaultB2C",
    );
    const boundContainers = new Set<HTMLElement>();

    blocks.forEach((block) => {
      this.getHorizontalScrollAncestors(block).forEach((container) => {
        if (boundContainers.has(container)) return;
        boundContainers.add(container);

        const onHorizontalScroll = () => {
          this.syncMobileFixedStickyPositions();
        };

        container.addEventListener("scroll", onHorizontalScroll, {
          passive: true,
        });
        this.horizontalScrollCleanups.push(() => {
          container.removeEventListener("scroll", onHorizontalScroll);
        });
      });
    });
  }

  private syncMobileFixedSticky(
    stickyEl: HTMLElement,
    block: HTMLElement | null,
    stuck: boolean,
    stickyTop: number,
  ): void {
    if (!this.usesMobileFixedSticky() || !block || !stuck) {
      this.clearMobileFixedSticky(stickyEl);
      return;
    }

    const blockRect = block.getBoundingClientRect();
    stickyEl.style.position = "fixed";
    stickyEl.style.top = `${stickyTop}px`;
    stickyEl.style.left = `${blockRect.left}px`;
    stickyEl.style.width = `${blockRect.width}px`;
    stickyEl.style.right = "auto";
    this.syncStickyPlaceholder(stickyEl, true);
  }

  private updateSearchStickyMode(): void {
    const search = this.getSearchEl();
    if (!search) return;

    const summaryVisible = this.isSummaryVisible();
    const desktopSummarySearch = this.usesDesktopSummarySearchMode();

    this.element.classList.toggle(
      SUMMARY_SEARCH_MODE_CLASS,
      desktopSummarySearch,
    );

    if (this.usesMobileSummaryHandoffMode()) {
      this.teardownSummaryModeWheelListener();
      this.lastScrollY = window.scrollY;
      this.resetMobileSummarySearchToHandoff(search);
      this.lastAppliedCardsStickyTop = -1;
      return;
    }

    if (summaryVisible) {
      this.lastScrollY = window.scrollY;
      this.summaryModeSearchRevealed = false;
      this.summaryModeJourneyScrollEngaged = false;
      this.summaryModeJourneyEngagedReady = false;
      this.lastJourneyLayoutStateKey = "";
      this.summaryModeRevealWhenUnlocked = false;
      this.summaryModeLayoutSuppressUntil = 0;
      this.searchHiddenByJourney = false;
      this.ensureSummaryModeSearchHeightCached(search);
      this.applySummaryModePreEngageSearch(search);
      this.setupSummaryModeWheelListener();
      this.summaryModeCardsOffsetDirty = true;
      this.lastAppliedCardsStickyTop = -1;
      return;
    }

    this.teardownSummaryModeWheelListener();

    this.summaryModeSearchRevealed = false;
    this.summaryModeJourneyScrollEngaged = false;
    this.summaryModeJourneyEngagedReady = false;
    this.clearSummaryModeSearchPresentation(search);
    search.classList.remove(SEARCH_NON_STICKY_CLASS);
    // Sin summary (p. ej. dispo-summary antes de seleccionar vuelo) el
    // buscador se ve en flujo; solo lo colapsa el handoff con el journey.
    if (!this.searchHiddenByJourney) {
      search.classList.remove(SEARCH_CURTAIN_COLLAPSED_CLASS);
    }

    this.setSearchRevealProgress(0, { animate: false });
    this.clearSummaryModeSearchMetrics();
    this.applyCardsStickyOffset(false);
  }

  private isSummaryModeSearchTransitionLocked(): boolean {
    return performance.now() < this.summaryModeSearchTransitionUntil;
  }

  private lockSummaryModeSearchTransition(): void {
    this.summaryModeSearchTransitionUntil =
      performance.now() +
      FlyAvailabilityLayout.SUMMARY_SEARCH_TRANSITION_LOCK_MS;
  }

  private setSearchRevealProgress(
    value: 0 | 1,
    options: { animate?: boolean } = {},
  ): void {
    const root = document.documentElement;
    const { animate = true } = options;

    if (!animate) {
      root.classList.remove(
        FlyAvailabilityLayout.SEARCH_MOTION_IN_CLASS,
        FlyAvailabilityLayout.SEARCH_MOTION_OUT_CLASS,
      );
      root.style.setProperty(
        FlyAvailabilityLayout.SEARCH_REVEAL_PROGRESS_VAR,
        String(value),
      );
      return;
    }

    root.style.setProperty(
      FlyAvailabilityLayout.SEARCH_REVEAL_PROGRESS_VAR,
      String(value),
    );
  }

  private beginSearchRevealMotion(direction: "in" | "out"): void {
    const root = document.documentElement;
    root.classList.remove(
      direction === "in"
        ? FlyAvailabilityLayout.SEARCH_MOTION_OUT_CLASS
        : FlyAvailabilityLayout.SEARCH_MOTION_IN_CLASS,
    );
    root.classList.add(
      direction === "in"
        ? FlyAvailabilityLayout.SEARCH_MOTION_IN_CLASS
        : FlyAvailabilityLayout.SEARCH_MOTION_OUT_CLASS,
    );
  }

  private endSearchRevealMotion(): void {
    document.documentElement.classList.remove(
      FlyAvailabilityLayout.SEARCH_MOTION_IN_CLASS,
      FlyAvailabilityLayout.SEARCH_MOTION_OUT_CLASS,
    );
  }

  private applySummaryModeSearchMetrics(search: HTMLElement): void {
    this.ensureSummaryModeSearchHeightCached(search);
    if (this.cachedSummaryModeSearchHeight <= 0) return;

    const heightValue = `${this.cachedSummaryModeSearchHeight}px`;
    this.element.style.setProperty(
      FlyAvailabilityLayout.SEARCH_REVEAL_MAX_H_VAR,
      heightValue,
    );
    search.style.setProperty(
      FlyAvailabilityLayout.SEARCH_REVEAL_MAX_H_VAR,
      heightValue,
    );
  }

  private clearSummaryModeSearchMetrics(): void {
    this.element.style.removeProperty(
      FlyAvailabilityLayout.SEARCH_REVEAL_MAX_H_VAR,
    );
    const search = this.getSearchEl();
    search?.style.removeProperty(FlyAvailabilityLayout.SEARCH_REVEAL_MAX_H_VAR);
  }

  /** Top sticky resuelto del summary (px); no usar la variable CSS cruda (puede ser calc()). */
  private getSummaryModeStickyTopPx(): number {
    const summary = this.getSummaryBar();
    if (!summary) return 0;

    const top = getComputedStyle(summary).top;
    if (!top || top === "auto") return 0;

    const px = parseFloat(top);
    return Number.isFinite(px) ? px : 0;
  }

  private attachSearchRevealMotionEndHandler(onComplete: () => void): void {
    const root = document.documentElement;
    let completed = false;

    const complete = () => {
      if (completed) return;
      completed = true;
      window.clearTimeout(fallbackTimer);
      this.detachSearchCurtainAnimationHandler(root);
      this.endSearchRevealMotion();
      onComplete();
    };

    const onMotionEnd = (event: TransitionEvent) => {
      if (
        event.target !== root ||
        event.propertyName !== FlyAvailabilityLayout.SEARCH_REVEAL_PROGRESS_VAR
      ) {
        return;
      }
      complete();
    };

    const fallbackTimer = window.setTimeout(
      complete,
      FlyAvailabilityLayout.SUMMARY_SEARCH_CURTAIN_FALLBACK_MS,
    );

    this.searchCurtainAnimationEndHandler = onMotionEnd;
    root.addEventListener("transitionend", onMotionEnd);
  }

  private clearSummaryModeFixedSearchGeometry(search: HTMLElement): void {
    search.style.removeProperty("left");
    search.style.removeProperty("width");
  }

  private isSummaryModeMobilePinned(search: HTMLElement): boolean {
    return (
      search.classList.contains(SEARCH_MOBILE_PINNED_CLASS) &&
      !search.classList.contains(SEARCH_CURTAIN_COLLAPSED_CLASS)
    );
  }

  private clearSummaryModeMobilePin(search: HTMLElement): void {
    search.classList.remove(SEARCH_MOBILE_PINNED_CLASS, SEARCH_STUCK_CLASS);
    if (!search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS)) {
      this.clearSummaryModeFixedSearchGeometry(search);
    }
  }

  private syncSummaryModeFixedSearchGeometry(search: HTMLElement): void {
    if (
      !search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS) &&
      !search.classList.contains(SEARCH_MOBILE_PINNED_CLASS)
    ) {
      return;
    }

    const wrapper = this.element.querySelector<HTMLElement>(
      ".fly-availability__wrapper",
    );
    const anchor = wrapper ?? this.element;
    const rect = anchor.getBoundingClientRect();

    search.style.left = `${rect.left}px`;
    search.style.width = `${rect.width}px`;
  }

  private clearSummaryModeSearchPresentation(search: HTMLElement): void {
    this.detachSearchCurtainAnimationHandler(search);
    search.classList.remove(
      SEARCH_SUMMARY_REVEALED_CLASS,
      SEARCH_MOBILE_PINNED_CLASS,
      SEARCH_SUMMARY_MEASURE_CLASS,
      SEARCH_CURTAIN_HIDDEN_CLASS,
      SEARCH_CURTAIN_REVEALING_CLASS,
      SEARCH_STUCK_CLASS,
    );
    this.clearSummaryModeFixedSearchGeometry(search);
    search.classList.add(SEARCH_CURTAIN_COLLAPSED_CLASS);
    this.setSearchRevealProgress(0, { animate: false });
    this.clearSummaryModeSearchMetrics();
  }

  private setupSummaryModeWheelListener(): void {
    if (!this.usesDesktopSummarySearchMode() || this.summaryModeWheelHandler) {
      return;
    }

    this.summaryModeWheelHandler = (event: WheelEvent) => {
      if (!this.isSummaryVisible() || !this.summaryModeJourneyScrollEngaged) {
        return;
      }
      if (performance.now() < this.summaryModeEngageSuppressUntil) return;
      if (performance.now() < this.summaryModeLayoutSuppressUntil) return;
      if (this.isSummaryModeSearchTransitionLocked()) return;
      if (
        this.usesMobileSummaryLayout() &&
        !this.isSummaryModeJourneyStickyInDom()
      ) {
        return;
      }

      const search = this.getSearchEl();
      if (!search) return;

      this.applySummaryModeScrollIntent(search, -event.deltaY);
    };

    window.addEventListener("wheel", this.summaryModeWheelHandler, {
      passive: true,
    });
  }

  /**
   * Positivo = scroll hacia arriba (mostrar). Negativo = hacia abajo (ocultar).
   * Usado por wheel y por el handler de scroll.
   */
  private applySummaryModeScrollIntent(
    search: HTMLElement,
    scrollDeltaUp: number,
  ): void {
    if (
      Math.abs(scrollDeltaUp) <
      FlyAvailabilityLayout.SUMMARY_SCROLL_INTENT_MIN_DELTA
    ) {
      return;
    }

    if (scrollDeltaUp > 0) {
      this.applySummaryModeSearchUserIntent(search, true);
    } else {
      this.applySummaryModeSearchUserIntent(search, false);
    }
  }

  private applySummaryModeSearchUserIntent(
    search: HTMLElement,
    reveal: boolean,
  ): void {
    // No ocultar el buscador mientras el formulario editable esté expandido.
    // El usuario solo debe verlo desaparecer al hacer scroll voluntariamente
    // tras cerrar el formulario, no como efecto colateral del toggle.
    if (
      !reveal &&
      search.classList.contains(
        "fly-availability__searchDispo-container--toggle-expanded",
      )
    ) {
      return;
    }

    search.classList.remove(SEARCH_NON_STICKY_CLASS);

    if (this.summaryModeSearchRevealed === reveal) {
      if (reveal) {
        this.syncSummaryModeFixedSearchGeometry(search);
      }
      return;
    }

    this.summaryModeSearchRevealed = reveal;
    this.summaryModeRevealWhenUnlocked = false;
    this.lockSummaryModeSearchTransition();
    this.summaryModeCardsOffsetDirty = true;
    this.lastScrollY = window.scrollY;

    if (reveal) {
      search.classList.remove(SEARCH_MOBILE_PINNED_CLASS);
      this.revealSummaryModeSearchWithCurtain(search, window.scrollY);
      return;
    }

    this.clearSummaryModeMobilePin(search);
    this.hideSummaryModeSearchWithCurtain(search);
  }

  private hideSummaryModeSearchWithCurtain(search: HTMLElement): void {
    if (!search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS)) {
      this.finalizeSummaryModeSearchHidden(search);
      return;
    }

    this.detachSearchCurtainAnimationHandler(search);
    search.classList.remove(
      SEARCH_CURTAIN_REVEALING_CLASS,
      SEARCH_CURTAIN_COLLAPSED_CLASS,
    );
    search.classList.add(SEARCH_CURTAIN_HIDDEN_CLASS);
    void search.offsetWidth;

    this.attachSearchRevealMotionEndHandler(() => {
      if (!search.classList.contains(SEARCH_CURTAIN_HIDDEN_CLASS)) return;
      this.finalizeSummaryModeSearchHidden(search);
      this.summaryModeCardsOffsetDirty = true;
      this.lastScrollY = window.scrollY;
    });

    this.beginSearchRevealMotion("out");
    this.setSearchRevealProgress(0);
  }

  private finalizeSummaryModeSearchHidden(search: HTMLElement): void {
    search.classList.remove(
      SEARCH_SUMMARY_REVEALED_CLASS,
      SEARCH_STUCK_CLASS,
      SEARCH_CURTAIN_HIDDEN_CLASS,
      SEARCH_CURTAIN_REVEALING_CLASS,
    );
    this.clearSummaryModeFixedSearchGeometry(search);
    search.classList.add(SEARCH_CURTAIN_COLLAPSED_CLASS);
    this.setSearchRevealProgress(0, { animate: false });
  }

  private revealSummaryModeSearchWithCurtain(
    search: HTMLElement,
    scrollY: number,
  ): void {
    this.ensureSummaryModeSearchHeightCached(search);
    const useFixedOverlay = scrollY > 0;

    this.detachSearchCurtainAnimationHandler(search);
    search.classList.remove(
      SEARCH_CURTAIN_COLLAPSED_CLASS,
      SEARCH_CURTAIN_REVEALING_CLASS,
    );

    if (!useFixedOverlay) {
      search.classList.remove(
        SEARCH_SUMMARY_REVEALED_CLASS,
        SEARCH_STUCK_CLASS,
        SEARCH_CURTAIN_HIDDEN_CLASS,
      );
      this.clearSummaryModeFixedSearchGeometry(search);
      this.setSearchRevealProgress(0, { animate: false });
      return;
    }

    search.classList.add(SEARCH_SUMMARY_REVEALED_CLASS, SEARCH_STUCK_CLASS);
    this.applySummaryModeSearchMetrics(search);
    this.syncSummaryModeFixedSearchGeometry(search);
    search.classList.remove(SEARCH_CURTAIN_REVEALING_CLASS);
    search.classList.add(SEARCH_CURTAIN_HIDDEN_CLASS);
    this.setSearchRevealProgress(0, { animate: false });
    void search.offsetWidth;

    this.attachSearchRevealMotionEndHandler(() => {
      if (!search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS)) return;
      search.classList.remove(
        SEARCH_CURTAIN_REVEALING_CLASS,
        SEARCH_CURTAIN_HIDDEN_CLASS,
      );
      this.lastScrollY = window.scrollY;
      requestAnimationFrame(() => {
        this.syncSummaryModeFixedSearchGeometry(search);
      });
    });

    search.classList.remove(SEARCH_CURTAIN_HIDDEN_CLASS);
    search.classList.add(SEARCH_CURTAIN_REVEALING_CLASS);
    this.beginSearchRevealMotion("in");

    requestAnimationFrame(() => {
      if (!search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS)) return;
      this.setSearchRevealProgress(1);
    });
  }

  private ensureSummaryModeSearchHeightCached(search: HTMLElement): void {
    if (this.cachedSummaryModeSearchHeight > 0) return;

    search.classList.add(SEARCH_SUMMARY_MEASURE_CLASS);
    this.cachedSummaryModeSearchHeight = Math.max(0, search.offsetHeight);
    search.classList.remove(SEARCH_SUMMARY_MEASURE_CLASS);
    // Con una medida de 0 (buscador aún sin layout) apply → ensure → apply
    // entraba en bucle infinito; se reintenta en la siguiente llamada.
    if (this.cachedSummaryModeSearchHeight > 0) {
      this.applySummaryModeSearchMetrics(search);
    }
  }

  /**
   * Fase previa al 1.er journey sticky: desktop = buscador en flujo;
   * mobile (≤810) = buscador sticky arriba (no hay summary superior).
   */
  private applySummaryModePreEngageSearch(
    search: HTMLElement,
    scrollY = window.scrollY,
  ): void {
    if (this.usesMobileSummaryLayout()) {
      this.applySummaryModeMobilePreEngageSearch(search, scrollY);
      return;
    }
    this.applySummaryModeSearchBaseline(search);
  }

  /**
   * ≤810px sin journey sticky activo: fija el buscador arriba al bajar (sticky
   * nativo se pierde al salir del contenedor del primer bloque).
   */
  private applySummaryModeMobilePreEngageSearch(
    search: HTMLElement,
    scrollY: number,
  ): void {
    const shouldPin = scrollY > 0;

    if (shouldPin && this.isSummaryModeMobilePinned(search)) {
      return;
    }

    if (
      !shouldPin &&
      !search.classList.contains(SEARCH_MOBILE_PINNED_CLASS) &&
      !search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS)
    ) {
      return;
    }

    this.detachSearchCurtainAnimationHandler(search);
    search.classList.remove(
      SEARCH_NON_STICKY_CLASS,
      SEARCH_CURTAIN_COLLAPSED_CLASS,
      SEARCH_CURTAIN_HIDDEN_CLASS,
      SEARCH_CURTAIN_REVEALING_CLASS,
      SEARCH_SUMMARY_REVEALED_CLASS,
    );
    this.setSearchRevealProgress(0, { animate: false });

    if (shouldPin) {
      this.ensureSummaryModeSearchHeightCached(search);
      search.classList.add(SEARCH_MOBILE_PINNED_CLASS, SEARCH_STUCK_CLASS);
      this.applySummaryModeSearchMetrics(search);
      this.summaryModeCardsOffsetDirty = true;
      return;
    }

    this.clearSummaryModeMobilePin(search);
    this.summaryModeCardsOffsetDirty = true;
  }

  /**
   * Desktop — inicio sin journey sticky: buscador en flujo;
   * solo el summary queda sticky al bajar.
   */
  private applySummaryModeSearchBaseline(search: HTMLElement): void {
    const needsUpdate =
      !search.classList.contains(SEARCH_NON_STICKY_CLASS) ||
      search.classList.contains(SEARCH_CURTAIN_COLLAPSED_CLASS) ||
      search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS) ||
      search.classList.contains(SEARCH_STUCK_CLASS);

    this.detachSearchCurtainAnimationHandler(search);
    search.classList.add(SEARCH_NON_STICKY_CLASS);
    search.classList.remove(
      SEARCH_SUMMARY_REVEALED_CLASS,
      SEARCH_MOBILE_PINNED_CLASS,
      SEARCH_CURTAIN_COLLAPSED_CLASS,
      SEARCH_CURTAIN_HIDDEN_CLASS,
      SEARCH_CURTAIN_REVEALING_CLASS,
      SEARCH_STUCK_CLASS,
    );
    this.clearSummaryModeFixedSearchGeometry(search);
    this.setSearchRevealProgress(0, { animate: false });

    if (needsUpdate) {
      this.summaryModeCardsOffsetDirty = true;
    }
  }

  private setSummaryModeSearchRevealed(
    search: HTMLElement,
    revealed: boolean,
    scrollY: number,
  ): void {
    this.detachSearchCurtainAnimationHandler(search);
    search.classList.remove(
      SEARCH_CURTAIN_HIDDEN_CLASS,
      SEARCH_CURTAIN_REVEALING_CLASS,
      SEARCH_SUMMARY_MEASURE_CLASS,
    );

    if (revealed) {
      this.ensureSummaryModeSearchHeightCached(search);
      search.classList.remove(SEARCH_CURTAIN_COLLAPSED_CLASS);

      const useFixedOverlay = scrollY > 0;

      search.classList.toggle(SEARCH_SUMMARY_REVEALED_CLASS, useFixedOverlay);
      this.clearSummaryModeFixedSearchGeometry(search);

      if (useFixedOverlay) {
        this.applySummaryModeSearchMetrics(search);
        this.syncSummaryModeFixedSearchGeometry(search);
        search.classList.add(SEARCH_STUCK_CLASS);
        this.setSearchRevealProgress(1, { animate: false });
      } else {
        search.classList.remove(SEARCH_STUCK_CLASS);
        this.setSearchRevealProgress(0, { animate: false });
      }
      return;
    }

    search.classList.remove(
      SEARCH_SUMMARY_REVEALED_CLASS,
      SEARCH_MOBILE_PINNED_CLASS,
      SEARCH_STUCK_CLASS,
    );
    this.clearSummaryModeFixedSearchGeometry(search);
    search.classList.add(SEARCH_CURTAIN_COLLAPSED_CLASS);
    this.setSearchRevealProgress(0, { animate: false });
  }

  /**
   * Summary visible:
   * - Desktop, antes del 1.er journey sticky: buscador en flujo; summary sticky arriba.
   * - Mobile (≤810), antes del 1.er journey sticky: buscador sticky arriba.
   * - Tras journey sticky: scroll/wheel arriba → buscador; abajo → solo journey sticky.
   */
  private updateSummaryModeSearchScroll(
    scrollY: number,
    journeyStickyInDom: boolean,
  ): void {
    const search = this.getSearchEl();
    if (!search) return;

    if (scrollY <= 0) {
      this.summaryModeJourneyScrollEngaged = false;
      this.summaryModeJourneyEngagedReady = false;
      this.summaryModeSearchRevealed = false;
      this.lastJourneyLayoutStateKey = "";
      this.summaryModeRevealWhenUnlocked = false;
      this.summaryModeLayoutSuppressUntil = 0;
      this.summaryModeLastJourneyStickyInDom = false;
      this.summaryModeMobileRepinDebounceUntil = 0;

      if (!this.isSummaryModeSearchTransitionLocked()) {
        this.applySummaryModePreEngageSearch(search, scrollY);
      }
      return;
    }

    const wasJourneySticky = this.summaryModeLastJourneyStickyInDom;

    if (journeyStickyInDom) {
      this.summaryModeJourneyScrollEngaged = true;
      this.summaryModeMobileRepinDebounceUntil = 0;
    } else if (
      this.usesMobileSummaryLayout() &&
      wasJourneySticky &&
      this.summaryModeJourneyScrollEngaged
    ) {
      this.summaryModeMobileRepinDebounceUntil =
        performance.now() + FlyAvailabilityLayout.MOBILE_REPIN_DEBOUNCE_MS;
    }

    this.summaryModeLastJourneyStickyInDom = journeyStickyInDom;

    if (!this.summaryModeJourneyScrollEngaged) {
      this.summaryModeSearchRevealed = false;

      if (!this.isSummaryModeSearchTransitionLocked()) {
        this.applySummaryModePreEngageSearch(search, scrollY);
      }
      return;
    }

    if (
      this.usesMobileSummaryLayout() &&
      !journeyStickyInDom &&
      !this.summaryModeSearchRevealed
    ) {
      const scrollDeltaUp = this.lastScrollY - scrollY;
      if (
        scrollDeltaUp < FlyAvailabilityLayout.SUMMARY_SCROLL_INTENT_MIN_DELTA
      ) {
        return;
      }
      if (performance.now() < this.summaryModeMobileRepinDebounceUntil) {
        return;
      }
      if (!this.isSummaryModeSearchTransitionLocked()) {
        this.applySummaryModeMobilePreEngageSearch(search, scrollY);
      }
      return;
    }

    search.classList.remove(
      SEARCH_NON_STICKY_CLASS,
      SEARCH_MOBILE_PINNED_CLASS,
    );

    if (!this.summaryModeJourneyEngagedReady) {
      this.summaryModeJourneyEngagedReady = true;
      this.summaryModeSearchRevealed = false;
      this.clearSummaryModeMobilePin(search);
      this.setSummaryModeSearchRevealed(search, false, scrollY);
      this.summaryModeCardsOffsetDirty = true;
      this.summaryModeEngageSuppressUntil =
        performance.now() +
        FlyAvailabilityLayout.SUMMARY_SEARCH_ENGAGE_SUPPRESS_MS;
      this.lastScrollY = scrollY;
      return;
    }

    const scrollDeltaUp = this.lastScrollY - scrollY;

    if (this.isSummaryModeSearchTransitionLocked()) {
      const root = document.documentElement;
      const hidingSearch = root.classList.contains(
        FlyAvailabilityLayout.SEARCH_MOTION_OUT_CLASS,
      );

      if (
        scrollDeltaUp <= -FlyAvailabilityLayout.SUMMARY_SCROLL_INTENT_MIN_DELTA
      ) {
        this.summaryModeRevealWhenUnlocked = false;
      } else if (
        scrollDeltaUp >=
          FlyAvailabilityLayout.SUMMARY_SCROLL_INTENT_MIN_DELTA &&
        !this.summaryModeSearchRevealed &&
        !hidingSearch
      ) {
        this.summaryModeRevealWhenUnlocked = true;
      }
      if (
        this.summaryModeSearchRevealed ||
        search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS)
      ) {
        this.syncSummaryModeFixedSearchGeometry(search);
      }
      return;
    }

    if (performance.now() < this.summaryModeEngageSuppressUntil) {
      if (
        this.summaryModeSearchRevealed ||
        search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS)
      ) {
        this.syncSummaryModeFixedSearchGeometry(search);
      }
      return;
    }

    if (performance.now() < this.summaryModeLayoutSuppressUntil) {
      this.summaryModeRevealWhenUnlocked = false;
      if (
        scrollDeltaUp <=
          -FlyAvailabilityLayout.SUMMARY_SCROLL_INTENT_MIN_DELTA &&
        this.summaryModeSearchRevealed &&
        !this.isSummaryModeSearchTransitionLocked()
      ) {
        this.applySummaryModeSearchUserIntent(search, false);
      } else if (
        this.summaryModeSearchRevealed ||
        search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS)
      ) {
        this.syncSummaryModeFixedSearchGeometry(search);
      }
      return;
    }

    if (
      this.summaryModeRevealWhenUnlocked &&
      !this.summaryModeSearchRevealed &&
      scrollDeltaUp >= FlyAvailabilityLayout.SUMMARY_SCROLL_INTENT_MIN_DELTA
    ) {
      this.summaryModeRevealWhenUnlocked = false;
      this.applySummaryModeSearchUserIntent(search, true);
    } else {
      if (scrollDeltaUp < 0) {
        this.summaryModeRevealWhenUnlocked = false;
      }
      this.applySummaryModeScrollIntent(search, scrollDeltaUp);
    }

    if (
      this.summaryModeSearchRevealed ||
      search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS)
    ) {
      this.syncSummaryModeFixedSearchGeometry(search);
    }
  }

  private getCardsStickyTop(journeyStickyActive: boolean): number {
    if (this.isSummaryVisible() && !this.usesMobileSummaryHandoffMode()) {
      const summaryHeight = this.getSummaryStickyStackHeight();

      if (journeyStickyActive) {
        return summaryHeight;
      }

      let searchHeight = 0;
      const search = this.getSearchEl();

      if (
        this.summaryModeJourneyScrollEngaged &&
        this.summaryModeSearchRevealed &&
        this.cachedSummaryModeSearchHeight > 0
      ) {
        searchHeight = this.cachedSummaryModeSearchHeight;
      } else if (
        this.usesMobileSummaryLayout() &&
        search &&
        !search.classList.contains(SEARCH_CURTAIN_COLLAPSED_CLASS) &&
        (!this.summaryModeJourneyScrollEngaged ||
          !this.isSummaryModeJourneyStickyInDom())
      ) {
        if (
          (search.classList.contains(SEARCH_SUMMARY_REVEALED_CLASS) ||
            search.classList.contains(SEARCH_MOBILE_PINNED_CLASS)) &&
          this.cachedSummaryModeSearchHeight > 0
        ) {
          searchHeight = this.cachedSummaryModeSearchHeight;
        } else if (!search.classList.contains(SEARCH_NON_STICKY_CLASS)) {
          searchHeight = search.offsetHeight;
        }
      }

      return Math.max(0, summaryHeight + searchHeight);
    }

    const stepper = this.getStepper();
    const stepperH =
      stepper && stepper.offsetHeight > 0 ? stepper.offsetHeight : 0;

    if (journeyStickyActive) {
      return stepperH;
    }

    const search = this.getSearchEl();
    let searchH = 0;
    if (search && !search.classList.contains(SEARCH_NON_STICKY_CLASS)) {
      searchH = search.offsetHeight;
    }

    return Math.max(0, searchH + stepperH);
  }

  /**
   * Mobile summary: el journey «cubre» el buscador cuando su bloque padre
   * ya ha subido hasta top ≤ 0 del viewport, de modo que al cambiar
   * cardsTop a 0 el journey puede sticky a top:0 sin salto.
   */
  private getMobileSummaryJourneyCoversSearch(): boolean {
    return (
      this.element.querySelector(
        `.fly-rtDefaultB2C .${STICKY_INFO_STUCK_CLASS}, .fly-rtFeaturedB2C .${STICKY_INFO_STUCK_CLASS}, .fly-owDefaultB2C > .${STICKY_INFO_STUCK_CLASS}`,
      ) !== null
    );
  }

  private applyCardsStickyOffset(journeyStickyActive: boolean): void {
    const root = document.documentElement;

    if (this.isSummaryVisible() && !this.usesMobileSummaryHandoffMode()) {
      const h = this.getCardsStickyTop(journeyStickyActive);
      const search = this.getSearchEl();
      let searchStackH = 0;

      if (
        search &&
        !journeyStickyActive &&
        !search.classList.contains(SEARCH_NON_STICKY_CLASS) &&
        !search.classList.contains(SEARCH_CURTAIN_COLLAPSED_CLASS)
      ) {
        searchStackH = search.offsetHeight;
      }

      if (Math.abs(h - this.lastAppliedCardsStickyTop) < 0.5) {
        return;
      }
      this.lastAppliedCardsStickyTop = h;
      root.style.setProperty(
        "--fly-dispo-search-stack-h",
        `${Math.max(0, searchStackH)}px`,
      );
      root.style.setProperty("--fly-dispo-cards-sticky-top", `${h}px`);
      return;
    }

    this.lastAppliedCardsStickyTop = -1;

    const search = this.getSearchEl();
    const searchStackH =
      search &&
      !search.classList.contains(SEARCH_NON_STICKY_CLASS) &&
      !journeyStickyActive
        ? search.offsetHeight
        : 0;
    const cardsTop = this.getCardsStickyTop(journeyStickyActive);

    root.style.setProperty(
      "--fly-dispo-search-stack-h",
      `${Math.max(0, searchStackH)}px`,
    );
    root.style.setProperty("--fly-dispo-cards-sticky-top", `${cardsTop}px`);
  }

  private detachSearchCurtainAnimationHandler(search?: HTMLElement): void {
    if (!this.searchCurtainAnimationEndHandler) return;

    const handler = this.searchCurtainAnimationEndHandler;
    document.documentElement.removeEventListener("transitionend", handler);
    document.documentElement.removeEventListener("animationend", handler);
    if (search) {
      search.removeEventListener("animationend", handler);
      search.removeEventListener("transitionend", handler);
    }
    this.searchCurtainAnimationEndHandler = null;
  }

  private clearJourneyExitRevealFallback(): void {
    if (this.journeyExitRevealFallbackTimer !== null) {
      window.clearTimeout(this.journeyExitRevealFallbackTimer);
      this.journeyExitRevealFallbackTimer = null;
    }
  }

  private detachJourneyExitRevealHandlers(): void {
    this.clearJourneyExitRevealFallback();

    if (!this.journeyExitRevealHandler) return;

    this.element
      .querySelectorAll<HTMLElement>(`.${STICKY_INFO_STOPPED_CLASS}`)
      .forEach((stickyEl) => {
        stickyEl.removeEventListener(
          "animationend",
          this.journeyExitRevealHandler!,
        );
      });
    this.journeyExitRevealHandler = null;
  }

  private completeSearchRevealAfterJourneyExit(): void {
    if (!this.searchHiddenByJourney) return;

    const search = this.getSearchEl();
    if (!search) return;

    this.searchHiddenByJourney = false;
    this.revealSearchWithCurtain(search);
  }

  private isAnyJourneyStoppedInDom(): boolean {
    return (
      this.element.querySelector(
        `.fly-rtDefaultB2C .${STICKY_INFO_STOPPED_CLASS}, .fly-rtFeaturedB2C .${STICKY_INFO_STOPPED_CLASS}, .fly-owDefaultB2C > .${STICKY_INFO_STOPPED_CLASS}`,
      ) !== null
    );
  }

  /**
   * Oculta el buscador de golpe (sin animación curtain-up) para evitar que
   * el cambio de altura dispare scroll-anchoring y provoque ciclos de
   * reveal/hide en el mobile handoff.
   */
  private instantHideSearch(search: HTMLElement): void {
    this.detachSearchCurtainAnimationHandler(search);
    this.detachJourneyExitRevealHandlers();
    search.classList.remove(SEARCH_CURTAIN_REVEALING_CLASS);
    search.classList.add(
      SEARCH_CURTAIN_HIDDEN_CLASS,
      SEARCH_CURTAIN_COLLAPSED_CLASS,
    );
  }

  private maintainSearchHidden(search: HTMLElement): void {
    search.classList.add(SEARCH_CURTAIN_HIDDEN_CLASS);
    search.classList.remove(SEARCH_CURTAIN_REVEALING_CLASS);
    if (!this.searchCurtainAnimationEndHandler) {
      search.classList.add(SEARCH_CURTAIN_COLLAPSED_CLASS);
    }
  }

  private hideSearchWithCurtain(search: HTMLElement): void {
    this.detachSearchCurtainAnimationHandler(search);
    this.detachJourneyExitRevealHandlers();
    search.classList.remove(
      SEARCH_CURTAIN_REVEALING_CLASS,
      SEARCH_CURTAIN_COLLAPSED_CLASS,
    );
    void search.offsetWidth;
    search.classList.add(SEARCH_CURTAIN_HIDDEN_CLASS);

    const onHideEnd = (event: AnimationEvent) => {
      if (
        event.target !== search ||
        event.animationName !== "fly-searchDispo-curtain-up"
      ) {
        return;
      }
      if (!search.classList.contains(SEARCH_CURTAIN_HIDDEN_CLASS)) return;
      search.classList.add(SEARCH_CURTAIN_COLLAPSED_CLASS);
      this.detachSearchCurtainAnimationHandler(search);
    };

    this.searchCurtainAnimationEndHandler = onHideEnd;
    search.addEventListener("animationend", onHideEnd);
  }

  private revealSearchWithCurtain(search: HTMLElement): void {
    this.detachSearchCurtainAnimationHandler(search);
    this.detachJourneyExitRevealHandlers();
    search.classList.remove(
      SEARCH_CURTAIN_HIDDEN_CLASS,
      SEARCH_CURTAIN_COLLAPSED_CLASS,
    );
    void search.offsetWidth;
    search.classList.add(SEARCH_CURTAIN_REVEALING_CLASS);

    const onRevealEnd = (event: AnimationEvent) => {
      if (
        event.target !== search ||
        event.animationName !== "fly-searchDispo-curtain-down"
      ) {
        return;
      }
      search.classList.remove(SEARCH_CURTAIN_REVEALING_CLASS);
      this.detachSearchCurtainAnimationHandler(search);
    };

    this.searchCurtainAnimationEndHandler = onRevealEnd;
    search.addEventListener("animationend", onRevealEnd);
  }

  private scheduleSearchRevealAfterJourneyExit(): void {
    if (!this.searchHiddenByJourney || this.journeyExitRevealHandler) return;

    const search = this.getSearchEl();
    if (!search) return;

    const stoppedStickies = this.element.querySelectorAll<HTMLElement>(
      `.fly-rtDefaultB2C .${STICKY_INFO_STOPPED_CLASS}, .fly-rtFeaturedB2C .${STICKY_INFO_STOPPED_CLASS}, .fly-owDefaultB2C > .${STICKY_INFO_STOPPED_CLASS}`,
    );

    if (stoppedStickies.length === 0) {
      this.completeSearchRevealAfterJourneyExit();
      return;
    }

    let pending = stoppedStickies.length;

    const finishReveal = () => {
      this.detachJourneyExitRevealHandlers();
      this.completeSearchRevealAfterJourneyExit();
    };

    const onJourneyExitEnd = (event: AnimationEvent) => {
      if (event.animationName !== "fly-stickyInfoB2C-curtain-up") return;

      pending -= 1;
      if (pending > 0) return;

      finishReveal();
    };

    this.journeyExitRevealHandler = onJourneyExitEnd;
    stoppedStickies.forEach((stickyEl) => {
      stickyEl.addEventListener("animationend", onJourneyExitEnd);
    });

    this.clearJourneyExitRevealFallback();
    this.journeyExitRevealFallbackTimer = window.setTimeout(() => {
      this.journeyExitRevealFallbackTimer = null;
      if (pending <= 0) return;
      finishReveal();
    }, 550);
  }

  /**
   * ≤810px + summary:
   * - Antes del handoff: buscador sticky visible hasta que el journey lo sustituye.
   * - Tras el handoff: al bajar NUNCA reaparece (incluso entre journeys).
   * - Solo reaparece si el usuario sube ≥ MOBILE_SCROLL_UP_REVEAL_PX por debajo
   *   del pico de scroll alcanzado.
   * - Si reaparece y vuelve a bajar ≥ threshold, se oculta de nuevo.
   */
  private updateMobileSummarySearchCurtain(
    journeyCoversSearch: boolean,
    scrollY: number,
  ): void {
    const search = this.getSearchEl();
    if (!search || search.classList.contains(SEARCH_NON_STICKY_CLASS)) {
      return;
    }

    if (scrollY <= 0) {
      this.mobileSummarySearchHandoffLatched = false;
      this.mobileSummaryPeakScrollY = 0;
      this.searchHiddenByJourney = false;
      this.detachSearchCurtainAnimationHandler(search);
      search.classList.remove(
        SEARCH_CURTAIN_HIDDEN_CLASS,
        SEARCH_CURTAIN_COLLAPSED_CLASS,
        SEARCH_CURTAIN_REVEALING_CLASS,
      );
      return;
    }

    // --- Pre-handoff: buscador visible hasta primer journey sticky ---
    if (!this.mobileSummarySearchHandoffLatched) {
      if (journeyCoversSearch && !this.searchHiddenByJourney) {
        this.applyCardsStickyOffset(true);
        this.updateBlockStickyShadow(0);
        this.lastAppliedCardsStickyTop = 0;
        this.mobileSummarySearchHandoffLatched = true;
        this.searchHiddenByJourney = true;
        this.mobileSummaryPeakScrollY = scrollY;
        this.lastScrollY = scrollY;
        this.instantHideSearch(search);
      }
      return;
    }

    // --- Post-handoff: estado basado en peakScrollY ---

    const goingDown = scrollY >= this.lastScrollY;

    if (this.searchHiddenByJourney) {
      // Pico solo crece mientras el buscador está oculto
      if (goingDown) {
        this.mobileSummaryPeakScrollY = Math.max(
          this.mobileSummaryPeakScrollY,
          scrollY,
        );
      }
      const distFromPeak = this.mobileSummaryPeakScrollY - scrollY;
      if (
        !goingDown &&
        distFromPeak >= FlyAvailabilityLayout.MOBILE_SCROLL_UP_REVEAL_PX
      ) {
        this.searchHiddenByJourney = false;
        this.mobileSummaryPeakScrollY = scrollY;
        this.revealSearchWithCurtain(search);
      } else {
        this.maintainSearchHidden(search);
      }
      return;
    }

    // Buscador visible (revelado por scroll-up):
    // peakScrollY se congela en el punto de reveal → re-ocultar si baja lo suficiente
    if (
      goingDown &&
      scrollY >
        this.mobileSummaryPeakScrollY +
          FlyAvailabilityLayout.MOBILE_SCROLL_UP_REVEAL_PX / 2
    ) {
      this.searchHiddenByJourney = true;
      this.mobileSummaryPeakScrollY = scrollY;
      this.applyCardsStickyOffset(true);
      this.updateBlockStickyShadow(0);
      this.instantHideSearch(search);
    }
  }

  private updateSearchCurtainState(journeyCoversSearch: boolean): void {
    const search = this.getSearchEl();
    if (!search || search.classList.contains(SEARCH_NON_STICKY_CLASS)) {
      if (!journeyCoversSearch) {
        this.searchHiddenByJourney = false;
      }
      return;
    }

    if (journeyCoversSearch) {
      if (!this.searchHiddenByJourney) {
        this.searchHiddenByJourney = true;
        this.hideSearchWithCurtain(search);
      } else {
        this.maintainSearchHidden(search);
      }
      return;
    }

    if (!this.searchHiddenByJourney) return;

    if (this.isAnyJourneyStoppedInDom()) {
      if (
        !this.journeyExitRevealHandler &&
        !this.journeyExitRevealFallbackTimer
      ) {
        this.scheduleSearchRevealAfterJourneyExit();
      }
      return;
    }

    this.searchHiddenByJourney = false;
    this.revealSearchWithCurtain(search);
  }

  private updateSearchStickyShadow(journeyStickyActive: boolean): void {
    const search = this.getSearchEl();
    if (!search) return;

    // Caso --non-sticky + --toggle-expanded: el contenedor tiene position:sticky
    // (aplicado por CSS) y necesita --stuck solo cuando está en su posición sticky
    // activa (scrollY suficiente para que haya llegado al top:0 del viewport).
    const isNonStickyExpanded =
      search.classList.contains(SEARCH_NON_STICKY_CLASS) &&
      search.classList.contains(
        "fly-availability__searchDispo-container--toggle-expanded",
      );

    if (isNonStickyExpanded) {
      const shouldStick =
        window.scrollY > 0 && search.getBoundingClientRect().top <= 0.5;
      search.classList.toggle(SEARCH_STUCK_CLASS, shouldStick);
      return;
    }

    if (search.classList.contains(SEARCH_NON_STICKY_CLASS)) return;

    if (journeyStickyActive) {
      search.classList.remove(SEARCH_STUCK_CLASS);
      return;
    }

    const shouldStick =
      window.scrollY > 0 && search.getBoundingClientRect().top <= 0.5;
    search.classList.toggle(SEARCH_STUCK_CLASS, shouldStick);
  }

  private updateSummaryStickyShadow(): void {
    if (!this.isSummaryVisible() || this.usesMobileSummaryLayout()) return;

    const summary = this.element.querySelector(".fly-summaryBarB2C");
    if (!summary) return;

    // Mientras el buscador editable está expandido, el summary usa position:relative
    // (vía CSS :has()) y top:auto, por lo que getSummaryModeStickyTopPx() devuelve 0
    // y el cálculo de shouldStick sería incorrecto. Se preserva el estado actual
    // para que --stuck (y su box-shadow) no se eliminen hasta que el buscador cierre.
    const searchEl = this.getSearchEl();
    if (
      searchEl?.classList.contains(
        "fly-availability__searchDispo-container--toggle-expanded",
      )
    ) {
      return;
    }

    const stickyTop = this.getSummaryModeStickyTopPx();
    const summaryTop = summary.getBoundingClientRect().top;
    const shouldStick =
      window.scrollY > 0 && Math.abs(summaryTop - stickyTop) <= 1.5;
    summary.classList.toggle(SUMMARY_STUCK_CLASS, shouldStick);
  }

  private isJourneyBlockStuck(
    block: HTMLElement | null,
    stickyEl: HTMLElement,
    stickyTop: number,
  ): boolean {
    if (window.scrollY <= 0) return false;

    const isActive = stickyEl.classList.contains(STICKY_INFO_STUCK_CLASS);
    const threshold =
      this.usesMobileSummaryLayout() && isActive
        ? stickyTop + FlyAvailabilityLayout.MOBILE_STICKY_HYSTERESIS_PX
        : stickyTop + 0.5;

    if (block) {
      return block.getBoundingClientRect().top <= threshold;
    }

    return stickyEl.getBoundingClientRect().top <= threshold;
  }

  private isAnyJourneyStickyInDom(): boolean {
    return (
      this.element.querySelector(
        `.fly-rtDefaultB2C .${STICKY_INFO_STUCK_CLASS}, .fly-rtFeaturedB2C .${STICKY_INFO_STUCK_CLASS}, .fly-owDefaultB2C > .${STICKY_INFO_STUCK_CLASS}`,
      ) !== null
    );
  }

  /**
   * Solo bloques RT/OW (no destacados): el engage del buscador en summary mobile
   * no debe dispararse con el sticky del carrusel featured.
   */
  private isSummaryModeJourneyStickyInDom(): boolean {
    return (
      this.element.querySelector(
        `.fly-rtDefaultB2C .${STICKY_INFO_STUCK_CLASS}, .fly-owDefaultB2C > .${STICKY_INFO_STUCK_CLASS}`,
      ) !== null
    );
  }

  private getJourneyLayoutStateKey(): string {
    const stuck = this.element.querySelectorAll(
      `.fly-rtDefaultB2C .${STICKY_INFO_STUCK_CLASS}, .fly-rtFeaturedB2C .${STICKY_INFO_STUCK_CLASS}, .fly-owDefaultB2C > .${STICKY_INFO_STUCK_CLASS}`,
    ).length;
    const stopped = this.element.querySelectorAll(
      `.fly-rtDefaultB2C .${STICKY_INFO_STOPPED_CLASS}, .fly-rtFeaturedB2C .${STICKY_INFO_STOPPED_CLASS}, .fly-owDefaultB2C > .${STICKY_INFO_STOPPED_CLASS}`,
    ).length;
    return `${stuck}:${stopped}`;
  }

  private parseJourneyLayoutCounts(layoutKey: string): {
    stuck: number;
    stopped: number;
  } {
    const [stuckRaw, stoppedRaw] = layoutKey.split(":");
    return {
      stuck: Number(stuckRaw) || 0,
      stopped: Number(stoppedRaw) || 0,
    };
  }

  /**
   * Al cambiar stuck/stopped el layout puede mover scrollY (incluso en el frame
   * siguiente por scroll anchoring). Resetea referencia y bloquea revelar.
   */
  private guardSummaryModeJourneyLayoutChange(scrollY: number): void {
    const layoutKey = this.getJourneyLayoutStateKey();
    if (layoutKey === this.lastJourneyLayoutStateKey) return;

    const prevKey = this.lastJourneyLayoutStateKey;
    const prev = this.parseJourneyLayoutCounts(prevKey);
    const next = this.parseJourneyLayoutCounts(layoutKey);
    this.lastJourneyLayoutStateKey = layoutKey;

    if (!this.summaryModeJourneyScrollEngaged) return;

    this.lastScrollY = scrollY;
    this.summaryModeRevealWhenUnlocked = false;
    this.summaryModeLayoutSuppressUntil =
      performance.now() + FlyAvailabilityLayout.SUMMARY_LAYOUT_SUPPRESS_MS;

    const search = this.getSearchEl();
    if (
      search &&
      next.stopped > prev.stopped &&
      this.summaryModeSearchRevealed &&
      !this.isSummaryModeSearchTransitionLocked()
    ) {
      this.applySummaryModeSearchUserIntent(search, false);
    }
  }

  /** Grupo RT siguiente (hermano con la misma clase de grupo), o null si es
   *  el último grupo o el bloque no es un grupo (OW). */
  private getNextStickyGroup(block: HTMLElement): HTMLElement | null {
    const groupSelector = ".fly-rtDefaultB2C__group, .fly-rtFeaturedB2C__group";
    if (!block.matches(groupSelector)) return null;

    let sibling = block.nextElementSibling;
    while (sibling) {
      if (sibling.matches(groupSelector)) return sibling as HTMLElement;
      sibling = sibling.nextElementSibling;
    }
    return null;
  }

  private updateBlockStickyShadow(stickyTop: number): boolean {
    const stickySelector =
      ".fly-rtDefaultB2C .fly-stickyInfoB2C, .fly-rtFeaturedB2C .fly-stickyInfoB2C, .fly-owDefaultB2C > .fly-stickyInfoB2C";

    let anyJourneyStickyActive = false;

    // Mientras el buscador editable está expandido, no aplicar estado sticky
    // al journey: se eliminan clases e inline-styles para que el elemento
    // vuelva a su posición natural en el flujo del documento.
    const searchEl = this.getSearchEl();
    const isToggleExpanded =
      searchEl?.classList.contains(
        "fly-availability__searchDispo-container--toggle-expanded",
      ) ?? false;

    this.element
      .querySelectorAll<HTMLElement>(stickySelector)
      .forEach((stickyEl) => {
        if (isToggleExpanded) {
          stickyEl.classList.remove(
            STICKY_INFO_STUCK_CLASS,
            STICKY_INFO_STOPPED_CLASS,
          );
          stickyEl.style.removeProperty("--fly-sticky-stop-top");
          this.clearMobileFixedSticky(stickyEl);
          return;
        }

        const block = stickyEl.closest(
          ".fly-rtDefaultB2C__group, .fly-rtFeaturedB2C__group, .fly-owDefaultB2C",
        ) as HTMLElement | null;
        const cards = block
          ? block.querySelectorAll(
              ".fly-gridRTB2C .fly-cardB2C, .fly-gridOWB2C .fly-cardB2C",
            )
          : null;
        const lastCard = cards && cards.length ? cards[cards.length - 1] : null;

        const isStuck = this.isJourneyBlockStuck(block, stickyEl, stickyTop);

        if (!block || !lastCard) {
          stickyEl.classList.remove(STICKY_INFO_STOPPED_CLASS);
          stickyEl.style.removeProperty("--fly-sticky-stop-top");
          stickyEl.classList.toggle(STICKY_INFO_STUCK_CLASS, isStuck);
          if (isStuck) anyJourneyStickyActive = true;
          this.syncMobileFixedSticky(stickyEl, block, isStuck, stickyTop);
          return;
        }

        const stickyHeight = stickyEl.offsetHeight;
        const blockTop = block.getBoundingClientRect().top + window.scrollY;
        const nextGroup = this.getNextStickyGroup(block);

        let stopAtDocumentTop: number;
        let shouldStop: boolean;

        if (nextGroup) {
          // Relevo entre grupos: este sticky sigue activo hasta que el del
          // grupo siguiente llega al top (mismo umbral que lo engancha en
          // isJourneyBlockStuck), para que nunca quede un tramo sin sticky.
          stopAtDocumentTop = blockTop + block.offsetHeight - stickyHeight;
          shouldStop = nextGroup.getBoundingClientRect().top <= stickyTop + 0.5;
        } else {
          const lastCardTop =
            (lastCard as HTMLElement).getBoundingClientRect().top +
            window.scrollY;
          stopAtDocumentTop = lastCardTop - stickyHeight;
          shouldStop = window.scrollY + stickyTop >= stopAtDocumentTop;
        }

        const isStuckActive = isStuck && !shouldStop;

        if (shouldStop) {
          const stopTopInBlock = Math.max(0, stopAtDocumentTop - blockTop);
          stickyEl.style.setProperty(
            "--fly-sticky-stop-top",
            `${stopTopInBlock}px`,
          );
          stickyEl.classList.add(STICKY_INFO_STOPPED_CLASS);
          this.clearMobileFixedSticky(stickyEl);
        } else {
          stickyEl.classList.remove(STICKY_INFO_STOPPED_CLASS);
          stickyEl.style.removeProperty("--fly-sticky-stop-top");
        }

        stickyEl.classList.toggle(STICKY_INFO_STUCK_CLASS, isStuckActive);
        if (isStuckActive) anyJourneyStickyActive = true;
        this.syncMobileFixedSticky(stickyEl, block, isStuckActive, stickyTop);
      });

    return anyJourneyStickyActive;
  }

  private updateAllStickyShadows(): void {
    if (this.usesJourneyNavStickyMode()) {
      this.runJourneyNavStickyTick();
      this.lastScrollY = window.scrollY;
      return;
    }

    const scrollY = window.scrollY;
    const summaryVisible = this.isSummaryVisible();

    if (!this.isSkeletonMode()) {
      const cardsStickyTop = this.getCardsStickyTop(false);

      if (this.usesMobileSummaryHandoffMode()) {
        this.runMobileSummaryStickyHandoffTick();
      } else if (summaryVisible) {
        this.guardSummaryModeJourneyLayoutChange(scrollY);
        if (this.summaryModeCardsOffsetDirty) {
          this.applyCardsStickyOffset(false);
          this.summaryModeCardsOffsetDirty = false;
        }
        const cardsStickyTopForJourney = this.getCardsStickyTop(false);
        this.updateBlockStickyShadow(cardsStickyTopForJourney);
        const journeyStickyInDom = this.isSummaryModeJourneyStickyInDom();
        this.updateSummaryModeSearchScroll(scrollY, journeyStickyInDom);
        if (this.summaryModeCardsOffsetDirty) {
          this.applyCardsStickyOffset(false);
          this.summaryModeCardsOffsetDirty = false;
          this.updateBlockStickyShadow(this.getCardsStickyTop(false));
        }
      } else {
        this.runStandardStickyHandoffTick(cardsStickyTop);
      }
    } else {
      this.applyCardsStickyOffset(false);
    }

    const journeyCoversSearch = this.isAnyJourneyStickyInDom();
    if (this.usesMobileSummaryHandoffMode()) {
      const mobileJourneyCoversSearch =
        this.getMobileSummaryJourneyCoversSearch();
      this.updateMobileSummarySearchCurtain(mobileJourneyCoversSearch, scrollY);
      if (
        !this.mobileSummarySearchHandoffLatched ||
        !this.searchHiddenByJourney
      ) {
        this.updateSearchStickyShadow(false);
      }
    } else if (!summaryVisible) {
      this.updateSearchCurtainState(journeyCoversSearch);
      this.updateSearchStickyShadow(journeyCoversSearch);
    } else {
      // summaryVisible: también necesario para el caso --non-sticky + --toggle-expanded
      this.updateSearchStickyShadow(journeyCoversSearch);
    }

    this.updateSummaryStickyShadow();

    this.lastScrollY = window.scrollY;
  }

  /**
   * Marca las cards que están realmente en el bottom del grid (las de la
   * columna más larga). Solo estas cards reciben el border-radius para casar
   * con el borde verde del contenedor. Las cards "last-child" de columnas
   * más cortas se mantienen planas porque no tocan el bottom del componente.
   */
  private markGridBottomCards(): void {
    const grids = this.element.querySelectorAll<HTMLElement>(
      ".fly-gridRTB2C, .fly-gridOWB2C",
    );

    grids.forEach((grid) => {
      const columnContents = Array.from(
        grid.querySelectorAll<HTMLElement>(
          ".fly-gridRTB2C__column-content, .fly-gridOWB2C__column-content",
        ),
      );

      grid
        .querySelectorAll<HTMLElement>(`.${CARD_GRID_BOTTOM_CLASS}`)
        .forEach((card) => card.classList.remove(CARD_GRID_BOTTOM_CLASS));

      if (columnContents.length === 0) return;

      const maxCardCount = columnContents.reduce((max, content) => {
        const count = content.querySelectorAll(".fly-cardB2C").length;
        return Math.max(max, count);
      }, 0);

      if (maxCardCount === 0) return;

      columnContents.forEach((content) => {
        const cards = content.querySelectorAll<HTMLElement>(".fly-cardB2C");
        if (cards.length !== maxCardCount) return;
        const lastCard = cards[cards.length - 1];
        if (lastCard) {
          lastCard.classList.add(CARD_GRID_BOTTOM_CLASS);
        }
      });
    });
  }

  /**
   * Animación tipo persiana del summary bar en mobile.
   *
   * - Entrada: la animación slide-up (definida en CSS) se reproduce
   *   automáticamente cuando el media query activa `display: block`.
   * - Salida: cuando la viewport vuelve a desktop, el media query oculta
   *   el elemento al instante (no transitionable). Aquí escuchamos el cambio
   *   y, antes de que se oculte, añadimos una clase que fuerza visibilidad y
   *   reproduce el slide-down. Al terminar la animación retiramos la clase
   *   para que el media query recupere el control.
   */
  private setupMobileSummaryAnimation(): void {
    if (typeof window.matchMedia !== "function") return;

    const summary = this.element.querySelector<HTMLElement>(
      ".fly-availability__summary-bar--mobile",
    );
    if (!summary) return;

    this.mobileSummaryMq = window.matchMedia(MOBILE_SUMMARY_MQ);

    this.mobileSummaryMqHandler = (event: MediaQueryListEvent) => {
      if (event.matches) {
        summary.classList.remove(MOBILE_SUMMARY_EXITING_CLASS);
        return;
      }

      if (this.mobileSummaryAnimationEndHandler) {
        summary.removeEventListener(
          "animationend",
          this.mobileSummaryAnimationEndHandler,
        );
        this.mobileSummaryAnimationEndHandler = null;
      }

      summary.classList.remove(MOBILE_SUMMARY_EXITING_CLASS);
      void summary.offsetWidth;
      summary.classList.add(MOBILE_SUMMARY_EXITING_CLASS);

      const onEnd = () => {
        summary.classList.remove(MOBILE_SUMMARY_EXITING_CLASS);
        if (this.mobileSummaryAnimationEndHandler === onEnd) {
          summary.removeEventListener("animationend", onEnd);
          this.mobileSummaryAnimationEndHandler = null;
        }
      };

      this.mobileSummaryAnimationEndHandler = onEnd;
      summary.addEventListener("animationend", onEnd);
    };

    this.mobileSummaryMq.addEventListener(
      "change",
      this.mobileSummaryMqHandler,
    );
  }

  private setupCardHoverDelay(): void {
    this.element
      .querySelectorAll<HTMLElement>(".fly-cardB2C")
      .forEach((card) => {
        let hoverTimer: number | null = null;

        const onEnter = () => {
          hoverTimer = window.setTimeout(() => {
            card.classList.add(CARD_HOVER_CLASS);
          }, CARD_HOVER_DELAY_MS);
        };

        const onLeave = () => {
          if (hoverTimer) {
            window.clearTimeout(hoverTimer);
            hoverTimer = null;
          }
          card.classList.remove(CARD_HOVER_CLASS);
        };

        card.addEventListener("mouseenter", onEnter);
        card.addEventListener("mouseleave", onLeave);

        this.cardHoverCleanups.push(() => {
          card.removeEventListener("mouseenter", onEnter);
          card.removeEventListener("mouseleave", onLeave);
          card.classList.remove(CARD_HOVER_CLASS);
        });
      });
  }
}

PluginRegistry.register("flyAvailabilityLayout", FlyAvailabilityLayout);
